/**
 * useAuth — single source of truth for auth state in the UI.
 *
 * Wraps Amplify's `getCurrentUser` + `fetchAuthSession` in a TanStack Query
 * so React subscribes to auth state without prop-drilling. Sign-in / out
 * mutations invalidate the query so every consumer rerenders together.
 */

import {
  getCurrentUser,
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  confirmSignIn as amplifyConfirmSignIn,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResendCode,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  type SignInInput,
  type SignUpInput,
  type ConfirmSignInInput,
  type ConfirmSignUpInput,
  type ResetPasswordInput,
  type ConfirmResetPasswordInput,
} from 'aws-amplify/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isConfigured } from '../../lib/env.ts';

const SESSION_KEY = ['auth', 'session'] as const;

export interface AuthSession {
  sub: string;
  email?: string;
  isAdmin: boolean;
}

async function fetchSession(): Promise<AuthSession | null> {
  if (!isConfigured()) return null;
  try {
    const u = await getCurrentUser();
    // signInDetails.loginId is the email-username typed at sign-in.
    const email = u.signInDetails?.loginId;
    // Group membership ships in the JWT, not in getCurrentUser. We resolve
    // it via fetchAuthSession claims — see lib/auth.ts.
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    const groupsRaw = session.tokens?.idToken?.payload['cognito:groups'];
    const groups = Array.isArray(groupsRaw) ? groupsRaw : [];
    return {
      sub: u.userId,
      email,
      isAdmin: groups.includes('admins'),
    };
  } catch {
    return null;
  }
}

export function useAuthSession() {
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: fetchSession,
    staleTime: 5 * 60_000,
  });
}

export function useSignIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SignInInput) => amplifySignIn(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: SESSION_KEY }),
  });
}

/**
 * confirmSignIn responds to a multi-step sign-in challenge. Used here for
 * imported users (`FORCE_CHANGE_PASSWORD`) who must set a new password on
 * their first sign-in — Cognito returns
 * `signInStep: 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'` and we send
 * the new password back via this call.
 */
export function useConfirmSignIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ConfirmSignInInput) => amplifyConfirmSignIn(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: SESSION_KEY }),
  });
}

export function useSignUp() {
  return useMutation({
    mutationFn: (input: SignUpInput) => amplifySignUp(input),
  });
}

export function useConfirmSignUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ConfirmSignUpInput) => amplifyConfirmSignUp(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: SESSION_KEY }),
  });
}

export function useResendSignUpCode() {
  return useMutation({
    mutationFn: (username: string) => amplifyResendCode({ username }),
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => amplifySignOut(),
    onSuccess: () => qc.setQueryData(SESSION_KEY, null),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (input: ResetPasswordInput) => amplifyResetPassword(input),
  });
}

export function useConfirmResetPassword() {
  return useMutation({
    mutationFn: (input: ConfirmResetPasswordInput) => amplifyConfirmResetPassword(input),
  });
}
